// pages/[slug].tsx
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from "next";
import React from "react";
import QueryString from "qs";
import dynamic from "next/dynamic";
const GhostPage = dynamic(() => import("@/Components/pages/GhostPage"));
const Itempage = dynamic(() => import("@/Components/pages/ItemPage"));

// Base URL from env
const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL ?? "";

// Define available page components
const components: Record<string, React.ComponentType<any>> = {
  Itempage: Itempage,
  GhostPage: GhostPage,
  // You can uncomment and add other components when needed
  // ServicePage: ServicePage,
  // TechnologyPage: TechnologyPage,
  // IndustryPage: IndustryPage,
  // StartUpPage: StartUpPage,
  // BlogPage: BlogPage,
  // CaseStudyPage: CaseStudyPage,
  // TemplatePage: TemplatePages,
  // UseCasePage: UseCasePage,
  // CrmPage: CrmPage,
  // HomePage: HomePage,
};

// Define types for page props
interface DynamicPageProps {
  component: string;
  apiResData: any;
  slug: string;
  listData: any[];
  referer: string;
  isNew: boolean;
  isMobile: boolean;
}

// ✅ getServerSideProps (with full typing)
export const getServerSideProps: GetServerSideProps<DynamicPageProps> = async (
  ctx: GetServerSidePropsContext
) => {
  const referer = (ctx.req?.headers?.referer as string) || "";

  if (process.env.NEXT_PUBLIC_CACHE_PAGES && ctx.res) {
    ctx.res.setHeader(
      "Cache-Control",
      "public, s-maxage=1800, stale-while-revalidate=59"
    );
  }

  const userAgent = (ctx.req?.headers["user-agent"] as string) || "";
  const isMobile =
    /iphone|ipod|android.*mobile|blackberry|iemobile|opera mini/i.test(
      userAgent
    );

  const slug = Array.isArray(ctx.params?.slug)
    ? ctx.params?.slug[0]
    : ctx.params?.slug || "";

  // Redirect conditions
  if (slug.includes("request-for-quote-proposal")) {
    return {
      redirect: {
        permanent: false,
        destination: "/request-proposal",
      },
    };
  }

  if (slug === "home") {
    return {
      redirect: { permanent: true, destination: "/" },
    };
  } else if (slug === "our-Leadership") {
    return {
      redirect: { permanent: true, destination: "/our-leadership" },
    };
  } else if (slug === "Services") {
    return {
      redirect: { permanent: true, destination: "/services" },
    };
  }

  // Fetch the component type by slug
  const res: any = await fetch(`${baseUrl}get-component-by-slug/${slug}`);
  const component = (await res.text()) || "Itempage";

  let apiRes: Response | null = null;
  let apiResData: any = null;
  let listData: any[] = [];

  // Create query
  const query = QueryString.stringify(
    {
      filters: {
        slug: {
          $eq: slug,
        },
      },
      populate: "deep, 8",
    },
    { encodeValuesOnly: false }
  );

  // Default API fetch
  switch (component) {
    case "Itempage":
      apiRes = await fetch(`${baseUrl}pages-v2s?${decodeURIComponent(query)}`);
      break;
    case "GhostPage":
      apiRes = await fetch(
        `${baseUrl}ghost-pages?${decodeURIComponent(query)}`
      );
      break;
    default:
      apiRes = await fetch(`${baseUrl}pages-v2s?${decodeURIComponent(query)}`);
      break;
  }

  // Handle API response
  if (apiRes) {
    apiResData = await apiRes.json();

    if (!apiResData?.data?.length) {
      return { notFound: true };
    }

    // Special case: careers page
    if (slug === "careers") {
      const jobQuery = QueryString.stringify(
        { populate: "deep" },
        { encodeValuesOnly: false }
      );
      const list = await fetch(
        `${baseUrl}position-vacancy?${decodeURIComponent(jobQuery)}`
      )
        .then((res: any) => res.json())
        .catch((err) => {
          console.error("Error fetching careers data:", err);
          return null;
        });

      listData = list?.data?.attributes?.vacancy ?? [];
    }
  } else {
    return { notFound: true };
  }

  return {
    props: {
      component,
      apiResData,
      slug,
      listData,
      referer,
      isNew: apiResData?.data?.[0]?.attributes?.design === "new",
      isMobile,
    },
  };
};

// ✅ React component
const DynamicPage: NextPage<DynamicPageProps> = ({
  component,
  apiResData,
  slug,
  referer,
  isMobile,
  listData,
}) => {
  const SelectedComponent = components[component];

  return (
    <div id="root">
      <div>
        {SelectedComponent && (
          <SelectedComponent
            slug={slug}
            data={apiResData}
            referer={referer}
            isNew={apiResData?.data?.[0]?.attributes?.design === "new"}
            listData={listData}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default DynamicPage;
